(() => {
  if (window.HNBottomSheet && window.HNBottomSheet.__version === '1.6.2') return;

  const SELECTOR_OPEN = '[data-hn-sheet-open]';
  const SELECTOR_SHEET = '[data-hn-sheet]';
  const SELECTOR_CLOSE = '[data-hn-sheet-close]';
  const SELECTOR_PANEL = '[data-hn-sheet-panel]';
  const SELECTOR_SCROLL = '.hn-sheet__content';
  const OPEN_CLASS = 'hn-sheet-open';
  const VERSION = '1.6.2';
  const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'textarea:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  let activeSheet = null;
  let previousFocus = null;
  let closeTimer = null;
  let lockedScrollY = 0;
  let savedBodyStyle = null;
  let savedHtmlStyle = null;
  let touchStartY = 0;

  const portalState = new WeakMap();

  function normalizeId(value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  function rawId(value) {
    return String(value || '').trim();
  }

  function cssEscape(value) {
    if (window.CSS && typeof window.CSS.escape === 'function') return window.CSS.escape(value);
    return String(value).replace(/"/g, '\\"');
  }

  function findSheet(id) {
    const raw = rawId(id);
    const normalized = normalizeId(id);
    const candidates = [];

    if (raw) candidates.push(raw);
    if (normalized && normalized !== raw) candidates.push(normalized);

    for (const candidate of candidates) {
      const sheet = document.querySelector(`${SELECTOR_SHEET}[data-hn-sheet="${cssEscape(candidate)}"]`)
        || document.getElementById(`HNBottomSheet-${candidate}`);

      if (sheet) return sheet;
    }

    return null;
  }

  function portalToBody(sheet) {
    if (!sheet || !(sheet instanceof HTMLElement)) return;
    if (sheet.parentElement === document.body) return;

    portalState.set(sheet, {
      parent: sheet.parentNode,
      nextSibling: sheet.nextSibling
    });

    document.body.appendChild(sheet);
  }

  function getFocusableElements(sheet) {
    return Array.from(sheet.querySelectorAll(FOCUSABLE_SELECTOR)).filter((element) => {
      if (!(element instanceof HTMLElement)) return false;
      return !element.hasAttribute('disabled') && element.getAttribute('aria-hidden') !== 'true';
    });
  }

  function focusFirstElement(sheet) {
    const closeButton = sheet.querySelector('.hn-sheet__close');
    const panel = sheet.querySelector(SELECTOR_PANEL);
    const focusable = getFocusableElements(sheet);
    const target = closeButton || focusable[0] || panel;

    if (target && typeof target.focus === 'function') target.focus({ preventScroll: true });
  }

  function saveCurrentStyles() {
    savedBodyStyle = {
      position: document.body.style.position,
      top: document.body.style.top,
      left: document.body.style.left,
      right: document.body.style.right,
      width: document.body.style.width,
      overflow: document.body.style.overflow
    };

    savedHtmlStyle = {
      overflow: document.documentElement.style.overflow,
      scrollBehavior: document.documentElement.style.scrollBehavior
    };
  }

  function lockPage() {
    if (document.documentElement.classList.contains(OPEN_CLASS)) return;

    lockedScrollY = window.scrollY || document.documentElement.scrollTop || document.body.scrollTop || 0;
    saveCurrentStyles();

    document.documentElement.classList.add(OPEN_CLASS);
    document.body.classList.add(OPEN_CLASS);

    document.documentElement.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${lockedScrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';
    document.body.style.overflow = 'hidden';
  }

  function unlockPage() {
    if (!document.documentElement.classList.contains(OPEN_CLASS)) return;

    document.documentElement.classList.remove(OPEN_CLASS);
    document.body.classList.remove(OPEN_CLASS);

    if (savedBodyStyle) {
      document.body.style.position = savedBodyStyle.position;
      document.body.style.top = savedBodyStyle.top;
      document.body.style.left = savedBodyStyle.left;
      document.body.style.right = savedBodyStyle.right;
      document.body.style.width = savedBodyStyle.width;
      document.body.style.overflow = savedBodyStyle.overflow;
    }

    if (savedHtmlStyle) {
      document.documentElement.style.overflow = savedHtmlStyle.overflow;
      document.documentElement.style.scrollBehavior = 'auto';
    }

    window.scrollTo(0, lockedScrollY);

    if (savedHtmlStyle) {
      window.requestAnimationFrame(() => {
        document.documentElement.style.scrollBehavior = savedHtmlStyle.scrollBehavior;
      });
    }

    savedBodyStyle = null;
    savedHtmlStyle = null;
  }

  function isScrollable(element) {
    return Boolean(element && element.scrollHeight > element.clientHeight + 1);
  }

  function getSheetScrollContainer(target) {
    if (!activeSheet || !(target instanceof Element)) return null;
    const scrollContainer = target.closest(SELECTOR_SCROLL);
    if (scrollContainer && activeSheet.contains(scrollContainer)) return scrollContainer;
    return null;
  }

  function shouldPreventScroll(target, deltaY) {
    const scrollContainer = getSheetScrollContainer(target);

    if (!scrollContainer) return true;
    if (!isScrollable(scrollContainer)) return true;

    const scrollTop = scrollContainer.scrollTop;
    const maxScrollTop = scrollContainer.scrollHeight - scrollContainer.clientHeight;
    const movingUp = deltaY < 0;
    const movingDown = deltaY > 0;

    if (movingUp && scrollTop <= 0) return true;
    if (movingDown && scrollTop >= maxScrollTop - 1) return true;

    return false;
  }

  function openSheet(id) {
    const sheet = findSheet(id);

    if (!sheet) {
      if (window.console && typeof window.console.warn === 'function') {
        window.console.warn('[HN Bottom Sheet] No sheet found for:', id);
      }
      return false;
    }

    if (closeTimer) {
      window.clearTimeout(closeTimer);
      closeTimer = null;
    }

    if (activeSheet && activeSheet !== sheet) closeSheet(false, true);

    portalToBody(sheet);

    previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    activeSheet = sheet;

    sheet.hidden = false;
    sheet.removeAttribute('hidden');
    sheet.setAttribute('aria-hidden', 'false');
    lockPage();

    window.requestAnimationFrame(() => {
      sheet.classList.add('is-active');
      focusFirstElement(sheet);
    });

    return true;
  }

  function closeSheet(restoreFocus = true, immediate = false) {
    if (!activeSheet) return false;

    const sheet = activeSheet;
    sheet.classList.remove('is-active');
    sheet.setAttribute('aria-hidden', 'true');
    unlockPage();
    activeSheet = null;

    const hideSheet = () => {
      sheet.hidden = true;
      sheet.setAttribute('hidden', '');
    };

    if (immediate) hideSheet();
    else closeTimer = window.setTimeout(hideSheet, 240);

    if (restoreFocus && previousFocus && typeof previousFocus.focus === 'function') {
      previousFocus.focus({ preventScroll: true });
    }

    previousFocus = null;
    return true;
  }

  function trapFocus(event) {
    if (!activeSheet || event.key !== 'Tab') return;

    const focusable = getFocusableElements(activeSheet);
    if (!focusable.length) {
      event.preventDefault();
      const panel = activeSheet.querySelector(SELECTOR_PANEL);
      if (panel) panel.focus({ preventScroll: true });
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus({ preventScroll: true });
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus({ preventScroll: true });
    }
  }

  document.addEventListener('click', (event) => {
    const openButton = event.target.closest(SELECTOR_OPEN);
    if (openButton) {
      event.preventDefault();
      openSheet(openButton.getAttribute('data-hn-sheet-open'));
      return;
    }

    if (activeSheet && event.target.closest(SELECTOR_CLOSE)) {
      event.preventDefault();
      closeSheet(true);
    }
  });

  document.addEventListener('keydown', (event) => {
    if (!activeSheet) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      closeSheet(true);
      return;
    }

    trapFocus(event);
  });

  document.addEventListener('wheel', (event) => {
    if (!activeSheet) return;
    if (shouldPreventScroll(event.target, event.deltaY)) event.preventDefault();
  }, { passive: false });

  document.addEventListener('touchstart', (event) => {
    if (!activeSheet || !event.touches || !event.touches.length) return;
    touchStartY = event.touches[0].clientY;
  }, { passive: true });

  document.addEventListener('touchmove', (event) => {
    if (!activeSheet || !event.touches || !event.touches.length) return;
    const deltaY = touchStartY - event.touches[0].clientY;
    if (shouldPreventScroll(event.target, deltaY)) event.preventDefault();
  }, { passive: false });

  document.addEventListener('shopify:section:unload', (event) => {
    if (!event.target) return;
    const sectionId = event.target.getAttribute('data-section-id') || event.target.id;
    if (!sectionId) return;

    document.querySelectorAll(SELECTOR_SHEET).forEach((sheet) => {
      const sheetId = sheet.getAttribute('data-hn-sheet') || '';
      if (sheetId.includes(normalizeId(sectionId))) sheet.remove();
    });
  });

  window.HNBottomSheet = {
    __version: VERSION,
    open: openSheet,
    close: closeSheet
  };
})();
